import { useEffect, useState } from "react";

import { Trans } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CalendarX, RotateCw, SearchX } from "lucide-react";

import { OutcomeState } from "@/components/account/outcome-state";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button-variants";
import { Skeleton } from "@/components/ui/skeleton";

interface ConsumeResponse {
  facilityId: string;
}

export const Route = createFileRoute("/_authenticated/account/invitations/token/$token")({
  component: FacilityInviteTokenPage,
});

/**
 * Magic-link landing for FacilityInviteToken.
 *
 * Legacy did the consume + redirect server-side (legacy
 * token/[token]/page.tsx): valid token → consume + redirect to
 * `/account/facility/<facilityId>`; already-consumed token → same redirect
 * (idempotent success); expired → "expired" message; unknown → "not found".
 * V1.0+ port calls apps/api `POST /api/account/facility-staff/consume-invite
 * { token }` and mirrors those outcomes — a 409 INVITE_CONSUMED carries the
 * facilityId so re-clicking the link still lands in the facility workspace.
 */
function FacilityInviteTokenPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // Bumped by "Try again" to re-run the consume call on a generic failure.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const goToFacility = (facilityId: string) =>
      navigate({
        to: "/account/facility/$facilityId",
        params: { facilityId },
        replace: true,
      });

    apiFetch
      .post<ConsumeResponse>("/api/account/facility-staff/consume-invite", { token })
      .then((res) => {
        if (cancelled) return;
        goToFacility(res.facilityId);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 409) {
            // INVITE_CONSUMED — legacy treated an already-consumed token as
            // idempotent success and redirected to the facility workspace.
            const body = err.body as { error?: string; facilityId?: string } | null;
            if (body?.error === "INVITE_CONSUMED") {
              if (body.facilityId) goToFacility(body.facilityId);
              else navigate({ to: "/account", replace: true });
              return;
            }
            setError("error");
          } else if (err.status === 404) setError("not_found");
          else if (err.status === 410) setError("expired");
          else setError("error");
        } else {
          setError("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, navigate, attempt]);

  if (error) {
    const accountCta = (
      <Link
        to="/account"
        className={buttonVariants({
          variant: error === "error" ? "outline" : "default",
          size: "touch",
        })}
      >
        <Trans>Go to my account</Trans>
      </Link>
    );
    return (
      <OutcomeState
        className="min-h-[calc(100dvh-4rem)] justify-center"
        icon={error === "not_found" ? SearchX : error === "expired" ? CalendarX : AlertTriangle}
        tone={error === "error" ? "destructive" : "warning"}
        title={
          error === "not_found" ? (
            <Trans>This invitation could not be found.</Trans>
          ) : error === "expired" ? (
            <Trans>This invitation has expired or has already been used.</Trans>
          ) : (
            <Trans>Could not accept the invitation. Please try again.</Trans>
          )
        }
        description={
          error === "error" ? undefined : (
            <Trans>Ask the person who invited you to send a new invitation.</Trans>
          )
        }
        actions={
          error === "error" ? (
            <>
              <Button
                size="touch"
                onClick={() => {
                  setError(null);
                  setAttempt((n) => n + 1);
                }}
              >
                <RotateCw className="size-4" aria-hidden />
                <Trans>Try again</Trans>
              </Button>
              {accountCta}
            </>
          ) : (
            accountCta
          )
        }
      />
    );
  }

  return (
    <div className="space-y-3 p-4">
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-4 w-64" />
      <p className="text-muted-foreground text-sm">
        <Trans>Accepting your facility invitation…</Trans>
      </p>
    </div>
  );
}
